import { useRef, useState } from "react";
import {
  BulkImportResult,
  BulkImportRowResult,
} from "../../../models/ClientModels";
import {
  bulkValidateClientsAsync,
  bulkImportClientsAsync,
} from "../../../api/ClientApi";
import { Constants } from "../../../constants/constants";
import "./BulkImportModal.css";

interface BulkImportModalProps {
  isOpen: boolean;
  organizationId: string;
  onClose: () => void;
  onImported: () => void;
}

type Step = "upload" | "preview" | "done";

const BulkImportModal = ({
  isOpen,
  organizationId,
  onClose,
  onImported,
}: BulkImportModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>("upload");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [importedCount, setImportedCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setStep("upload");
    setSelectedFile(null);
    setResult(null);
    setImportedCount(0);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (ext !== "csv" && ext !== "xlsx" && ext !== "xls") {
      setError("Please upload a CSV or Excel (.xlsx) file.");
      return;
    }
    setSelectedFile(file);
    setError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleValidate = async () => {
    if (!selectedFile) return;
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setValidating(true);
    setError(null);
    try {
      const res = await bulkValidateClientsAsync(token, organizationId, selectedFile);
      if (res.success && res.result) {
        setResult(res.result);
        setStep("preview");
      } else {
        setError(res.message || "Validation failed");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to validate file. Please try again.");
    } finally {
      setValidating(false);
    }
  };

  const handleImport = async () => {
    if (!result) return;
    const validRows = result.rows
      .filter((r) => r.isValid && r.data)
      .map((r) => r.data!);
    if (validRows.length === 0) return;

    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setImporting(true);
    setError(null);
    try {
      const res = await bulkImportClientsAsync(token, {
        organizationId,
        rows: validRows,
      });
      if (res.success && res.result) {
        setImportedCount(res.result.validRows);
        setStep("done");
        onImported();
      } else {
        setError(res.message || "Import failed");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to import clients. Please try again.");
    } finally {
      setImporting(false);
    }
  };

  const renderRow = (row: BulkImportRowResult) => (
    <tr key={row.rowNumber} className={row.isValid ? "valid" : "invalid"}>
      <td>{row.rowNumber}</td>
      <td>{row.data?.fullName || "\u2014"}</td>
      <td>{row.data?.phone || "\u2014"}</td>
      <td>{row.data?.email || "\u2014"}</td>
      <td>{row.data?.gender || "\u2014"}</td>
      <td>{row.data?.age ?? "\u2014"}</td>
      <td>{row.data?.city || "\u2014"}</td>
      <td>
        {row.isValid ? (
          <span className="bulk-badge bulk-badge--valid">Valid</span>
        ) : (
          <span className="bulk-badge bulk-badge--error">
            {row.errors.join(", ")}
          </span>
        )}
      </td>
    </tr>
  );

  if (!isOpen) return null;

  return (
    <div className="bulk-modal-overlay">
      <div className="bulk-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bulk-modal__header">
          <h3>Import Clients</h3>
          <button className="bulk-modal__close" onClick={handleClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="bulk-modal__body">
          {step === "upload" && (
            <div className="bulk-upload">
              <p className="bulk-upload__hint">
                Upload a CSV or Excel file (.xlsx). The first row should be
                headers. Column order does not matter &mdash; we match by name.
              </p>

              <div
                className={`bulk-dropzone ${dragOver ? "bulk-dropzone--active" : ""} ${selectedFile ? "bulk-dropzone--selected" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="bulk-dropzone__input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                  }}
                />
                {selectedFile ? (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span className="bulk-dropzone__name">{selectedFile.name}</span>
                    <span className="bulk-dropzone__size">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </span>
                    <button
                      className="bulk-dropzone__remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                      }}
                    >
                      Remove
                    </button>
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <span>Drag & drop a file here, or click to browse</span>
                    <span className="bulk-dropzone__formats">CSV, XLSX</span>
                  </>
                )}
              </div>

              {error && <p className="bulk-error">{error}</p>}

              <div className="bulk-modal__actions">
                <button className="bulk-btn bulk-btn--secondary" onClick={handleClose}>
                  Cancel
                </button>
                <button
                  className="bulk-btn bulk-btn--primary"
                  disabled={!selectedFile || validating}
                  onClick={handleValidate}
                >
                  {validating ? "Validating..." : "Next: Preview"}
                </button>
              </div>
            </div>
          )}

          {step === "preview" && result && (
            <div className="bulk-preview">
              <div className="bulk-summary">
                <span className="bulk-summary__item bulk-summary__total">
                  Total: {result.totalRows}
                </span>
                <span className="bulk-summary__item bulk-summary__valid">
                  Valid: {result.validRows}
                </span>
                <span className="bulk-summary__item bulk-summary__error">
                  Errors: {result.errorRows}
                </span>
              </div>

              <div className="bulk-table-wrap">
                <table className="bulk-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Gender</th>
                      <th>Age</th>
                      <th>City</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>{result.rows.map(renderRow)}</tbody>
                </table>
              </div>

              {error && <p className="bulk-error">{error}</p>}

              <div className="bulk-modal__actions">
                <button
                  className="bulk-btn bulk-btn--secondary"
                  onClick={() => {
                    reset();
                  }}
                >
                  Back
                </button>
                <button className="bulk-btn bulk-btn--secondary" onClick={handleClose}>
                  Cancel
                </button>
                <button
                  className="bulk-btn bulk-btn--primary"
                  disabled={importing || result.validRows === 0}
                  onClick={handleImport}
                >
                  {importing
                    ? "Importing..."
                    : `Import ${result.validRows} Client${result.validRows !== 1 ? "s" : ""}`}
                </button>
              </div>
            </div>
          )}

          {step === "done" && (
            <div className="bulk-done">
              <div className="bulk-done__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h4>Import Complete</h4>
              <p>
                {importedCount} client{importedCount !== 1 ? "s" : ""} imported
                successfully.
              </p>
              <div className="bulk-modal__actions">
                <button className="bulk-btn bulk-btn--primary" onClick={handleClose}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;
